import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ArrowLeft, Award, ExternalLink, MapPin, Star } from 'lucide-react';
import { getProAcademy, getProOpleider } from '../services/professionalAcademyApi';
import type { ProTraining } from '../services/professionalAcademyApi';
import { ProAcademyRij } from '../components/proAcademy/ProAcademyRij';
import { ProAcademyDetailModal } from '../components/proAcademy/ProAcademyDetailModal';

/**
 * Laag C: Opleiders-profielpagina van de Professional Academy — koptelefoon (logo, header,
 * certificeringslabels/erkenningsnummers), bio, leaderboard-score en een dedicated Netflix-rij
 * met uitsluitend de bijscholingen en workshops van deze aanbieder.
 */
export const ProfessionalAcademyOpleiderPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { id } = useParams<{ id: string }>();
  const providerId = Number(id);
  const [open, setOpen] = useState<ProTraining | null>(null);

  const profielQuery = useQuery({
    queryKey: ['pro-academy-opleider', providerId],
    queryFn: () => getProOpleider(providerId),
    enabled: Number.isInteger(providerId) && providerId > 0,
  });
  // Wallet voor de prijsopbouw in het Informatie-scherm (gedeelde cache met de Academy-pagina).
  const academyQuery = useQuery({ queryKey: ['pro-academy'], queryFn: getProAcademy });
  const p = profielQuery.data;

  const terug = (
    <Link to="/opleidingen" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900">
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      {t('proAcademy.opleider.terug', { defaultValue: 'Terug naar de Professional Academy' })}
    </Link>
  );

  if (profielQuery.isLoading) {
    return (
      <div className="space-y-4">
        {terug}
        <div className="h-56 animate-pulse rounded-2xl bg-white" />
      </div>
    );
  }
  if (!p) {
    const bericht = isAxiosError(profielQuery.error) && (profielQuery.error.response?.data as { message?: string } | undefined)?.message;
    return (
      <div className="space-y-4">
        {terug}
        <p className="rounded-2xl border border-gray-200 bg-white p-5 text-sm text-gray-600" role="alert">
          {bericht || t('proAcademy.opleider.nietGevonden', { defaultValue: 'Deze opleider is niet gevonden.' })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {terug}

      <header className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200">
        <div className="relative h-44 bg-[radial-gradient(120%_120%_at_20%_0%,#1d4ed855_0%,#0b1020_55%,#05070c_100%)] sm:h-60">
          {p.header_url ? (
            <img src={p.header_url} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="absolute inset-0 bg-[radial-gradient(120%_120%_at_20%_0%,#1d4ed855_0%,#0b1020_55%,#05070c_100%)]" aria-hidden="true" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-transparent" aria-hidden="true" />
        </div>
        <div className="relative -mt-16 flex flex-col gap-4 px-5 pb-5 sm:-mt-20 sm:flex-row sm:items-end sm:px-8">
          <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-xl ring-4 ring-white">
            {p.logo_url ? (
              <img src={p.logo_url} alt={p.organisatie_naam} className="h-full w-full object-contain p-2" />
            ) : (
              <span className="text-3xl font-extrabold text-[#0B1F3A]" aria-hidden="true">
                {p.organisatie_naam.charAt(0)}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">{t('proAcademy.opleider.erkend', { defaultValue: 'Erkende aanbieder' })}</p>
            <h1 className="text-2xl font-extrabold text-gray-900 sm:text-3xl">{p.organisatie_naam}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
              {p.stad && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-4 w-4" aria-hidden="true" />
                  {p.stad}
                </span>
              )}
              {p.website && (
                <a href={p.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-700 hover:text-sky-900">
                  {t('proAcademy.opleider.website', { defaultValue: 'Website' })}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              )}
            </div>
            {p.certificeringen.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-2" aria-label={t('proAcademy.opleider.certificeringen', { defaultValue: 'Certificeringen en erkenningen' })}>
                {p.certificeringen.map((c) => (
                  <li key={c} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                    <Award className="h-3.5 w-3.5" aria-hidden="true" />
                    {c}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Leaderboard: geaggregeerde platformscore over het hele portfolio */}
          <div className="shrink-0 rounded-2xl border border-gray-200 bg-white/90 px-5 py-3 text-center shadow-sm backdrop-blur">
            {p.score ? (
              <>
                <p className="inline-flex items-center gap-1.5 text-3xl font-extrabold text-gray-900">
                  <Star className="h-6 w-6 fill-amber-400 text-amber-400" aria-hidden="true" />
                  {p.score.gemiddelde.toLocaleString('nl-NL', { minimumFractionDigits: 1 })}
                </p>
                <p className="text-xs text-gray-500">
                  {t('proAcademy.opleider.over', { defaultValue: 'over {{n}} cursussen · {{reviews}} reviews', n: p.score.cursussen, reviews: p.score.aantal })}
                </p>
              </>
            ) : (
              <p className="max-w-[10rem] text-xs text-gray-500">{t('proAcademy.opleider.nogGeenScore', { defaultValue: 'Platformscore zichtbaar vanaf 5 reviews' })}</p>
            )}
          </div>
        </div>
      </header>

      {p.bio?.trim() && (
        <section className="max-w-3xl">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{t('proAcademy.opleider.overOns', { defaultValue: 'Over dit instituut' })}</h2>
          {/* Bio is platte tekst van de opleider: nooit als HTML renderen. */}
          <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-gray-700">{p.bio.trim()}</p>
        </section>
      )}

      {p.trainingen.length > 0 ? (
        <ProAcademyRij
          id={`opleider-${p.id}`}
          titel={t('proAcademy.opleider.catalogus', { defaultValue: 'Alle bijscholingen van {{naam}}', naam: p.organisatie_naam })}
          subtitel={t('proAcademy.opleider.aantal', { defaultValue: '{{n}} beschikbaar', n: p.trainingen.length })}
          trainingen={p.trainingen}
          onOpen={setOpen}
        />
      ) : (
        <p className="rounded-2xl border border-gray-200 bg-white p-5 text-sm text-gray-600">
          {t('proAcademy.opleider.geenBeschikbaar', { defaultValue: 'Er zijn op dit moment geen boekbare cursussen van deze opleider.' })}
        </p>
      )}

      {open && <ProAcademyDetailModal training={open} wallet={academyQuery.data?.wallet} onClose={() => setOpen(null)} />}
    </div>
  );
};

export default ProfessionalAcademyOpleiderPage;
